import { Field, Form, Formik, FormikHelpers } from 'formik';
import React, { useEffect, useState } from 'react';
import toastMessageServiceSingleton from 'src/services/toast-message.service';
import { MiniAppFocusWrapper } from '../miniapp-focus-wrapper/miniapp-focus-wrapper';
import OtpInput from '../otp-input/otp-input';
import { addMask, formatMobileNumber } from 'src/helpers/formatting';
import { DotLoader } from '../dot-loader/dot-loader';
import useMerchantProfile from 'src/hooks/useMerchantProfile';
import { validateUpdateBankingDetails } from 'src/services/merchant-profile.service';
import TealiumEvents from 'src/helpers/tealium-events';

interface IBankingDetailsOTPForm {
  merchantOtp: string;
}

type BankingDetailsOTPFormProps = {
  setShowSuccess: React.Dispatch<React.SetStateAction<boolean>>;
  onRequestNewOTP: () => void;
};

function BankingDetailsOTPForm({ setShowSuccess, onRequestNewOTP }: BankingDetailsOTPFormProps) {
  const otpTriesMax = 3;
  const otpLength = 5;
  const { merchantBankDetails } = useMerchantProfile();
  const [otpTries, setOtpTries] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);

  const otpTriesExceeded = () => otpTries >= otpTriesMax;

  const handleIncorrectOtp = () => {
    const otpRetriesCurrent = otpTries + 1;
    setOtpTries(otpRetriesCurrent);

    useEffect(() => TealiumEvents.bankingDetailsOtpModal(), []);
    if (otpRetriesCurrent >= otpTriesMax) {
      toastMessageServiceSingleton.addError('OTP attempts exceeded', null, '', true, null, true);
      TealiumEvents.otpLimitExceeded();
    } else {
      toastMessageServiceSingleton.addError(
        `Incorrect OTP - Try again ${otpRetriesCurrent}/${otpTriesMax}`,
        null,
        '',
        true,
        null,
        true,
      );
      TealiumEvents.updateBankingDetailsAttemptLeft(
        otpRetriesCurrent,
        `Incorrect OTP - Try again ${otpRetriesCurrent}/${otpTriesMax}`,
      );
    }
  };

  const submitOtp = async (
    values: IBankingDetailsOTPForm,
    helpers: FormikHelpers<IBankingDetailsOTPForm>,
  ) => {
    const { setSubmitting, resetForm } = helpers;
    setSubmitting(true);
    TealiumEvents.updateBankingDetailsContinueButton();
    if (!merchantBankDetails.requestId) {
      toastMessageServiceSingleton.addError(
        `Something happened please try again`,
        null,
        '',
        false,
        null,
        true,
      );
      return;
    }

    try {
      const abortController = new AbortController();
      setLoading(true);
      const result = await validateUpdateBankingDetails(
        abortController,
        { otp: values.merchantOtp },
        merchantBankDetails.requestId,
      );

      if (!result.ok) {
        setLoading(false);
        handleIncorrectOtp();
        resetForm();
        return;
      }

      setShowSuccess(true);
    } catch (error) {
      console.error(error);
      setLoading(false);
    }
  };

  const maskText = (text: string, type: 'tel' | 'email') => {
    switch (type) {
      case 'tel':
        const clean = formatMobileNumber(text);
        return addMask(clean, '*', 3, 6);
      case 'email':
        return addMask(text, '*', 2, text.indexOf('@') - 1);
      default:
        return text;
    }
  };

  const handleRequestNewOTP = () => {
    setOtpTries(0);
    onRequestNewOTP();
  };

  return (
    <>
      <h1 className="text-4xl text-center text-grey-600">We sent you an email</h1>
      <p className="w-full max-w-lg text-xl text-center text-grey-400">
        Please enter 4 digit verification
        <br />
        code sent to <strong>
          {maskText(merchantBankDetails.mobilePhoneNumber, 'tel')}
        </strong> and <strong>{maskText(merchantBankDetails.emailAddress, 'email')}</strong>
      </p>

      <Formik<IBankingDetailsOTPForm> initialValues={{ merchantOtp: '' }} onSubmit={submitOtp}>
        {({ values }) => (
          <Form>
            <Field
              key={otpTries}
              name="merchantOtp"
              component={MiniAppFocusWrapper}
              innerComponent={OtpInput}
              numInputs={otpLength}
              shouldAutoFocus={true}
              autoFocus
              isDisabled={otpTriesExceeded() || loading}
            />
            <div className="flex flex-col items-center gap-4 pt-8">
              <p className="text-base text-grey-600">
                {otpTries + 1 > otpTriesMax
                  ? 'OTP attempts exceeded'
                  : `Attempt ${otpTries + 1} of ${otpTriesMax}`}
              </p>
              {!otpTriesExceeded() && (
                <button
                  className="flex items-center justify-center w-full max-w-sm text-white bg-red-500 h-11 disabled:bg-white disabled:text-grey-500 disabled:border-2 disabled:border-solid disabled:border-grey-100 disabled:cursor-not-allowed rounded-40"
                  type="submit"
                  disabled={values.merchantOtp.length !== otpLength || loading}
                >
                  {loading ? <DotLoader /> : 'Continue'}
                </button>
              )}
            </div>
          </Form>
        )}
      </Formik>
      {!otpTriesExceeded() ? (
        <p className="text-base text-grey-600 2xs:text-center">
          Haven't yet received OTP yet?{' '}
          <button
            onClick={handleRequestNewOTP}
            className="text-red-500 cursor-pointer hover:underline"
          >
            Request new OTP.
          </button>
        </p>
      ) : (
        <p className="text-base text-center text-grey-600">
          <strong>Need assistance?</strong>
          <br />
          Please contact our{' '}
          <a href="tel:0800000654" className="text-red-500 underline cursor-pointer">
            Call Centre on 0800 000 654{' '}
          </a>
          or try again later
        </p>
      )}
    </>
  );
}

export default BankingDetailsOTPForm;
